// File: GraduateAdmissionApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function GraduateAdmissionApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // Program
    startTerm: '',
    degreeSought: '', // MS | MA | MEng | MBA | MPH | MEd | PhD | EdD | Other
    programName: '',
    concentration: '',
    delivery: '', // Campus | Online | Hybrid
    fundingInterest: 'None', // None | Graduate Assistantship (GTA/GRA) | Fellowships
    applyFinancialAid: 'Yes', // Yes | No

    // Personal info
    legalFirst: '',
    legalMiddle: '',
    legalLast: '',
    preferredName: '',
    dob: '',
    gender: '',
    ssnLast4: '',

    // Contact
    email: '',
    mobile: '',
    allowText: true,

    // Address
    homeStreet: '',
    homeApt: '',
    homeCity: '',
    homeState: '',
    homeZip: '',
    mailingSameAsHome: true,
    mailingStreet: '',
    mailingApt: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',

    // Citizenship / Residency
    citizenship: '',
    countryCitizenship: '',
    visaType: '',
    gtsResident: '', // Yes | No

    // Testing
    requestTestWaiver: true, // default demo-friendly
    greVerbal: '',
    greQuant: '',
    greAW: '',
    gmatTotal: '',
    toeflTotal: '',
    ieltsOverall: '',
    testDate: '',

    // Academic background summary
    highestDegree: '', // BA/BS | MA/MS | Other
    highestGPA: '',

    // Employment & Recs toggles
    includeEmployment: false,
    includeRecs: true,

    // Essays
    statementOfPurpose: '',
    researchInterests: '',
    resumeLink: '',

    // Conduct (optional)
    academicDiscipline: '',
    academicDisciplineExplain: '',

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  // Dynamic lists
  const [colleges, setColleges] = useState([
    { institution: '', city: '', state: '', degree: '', major: '', start: '', end: '', gpa: '' }
  ]);
  const [recs, setRecs] = useState([
    { name: '', email: '', relationship: '' }
  ]);
  const [employment, setEmployment] = useState([
    { employer: '', title: '', dates: '', responsibilities: '' }
  ]);

  // Helpers
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'mailingSameAsHome') {
      const next = checked;
      setForm((f) => {
        const updated = { ...f, mailingSameAsHome: next };
        if (next) {
          updated.mailingStreet = f.homeStreet;
          updated.mailingApt = f.homeApt;
          updated.mailingCity = f.homeCity;
          updated.mailingState = f.homeState.toUpperCase();
          updated.mailingZip = f.homeZip;
        }
        return updated;
      });
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (type === 'checkbox') {
      setForm((f) => ({ ...f, [name]: checked }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: value.toUpperCase() }));
      setErrors((ep) => ({ ...ep, [name]: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const updateRow = (rows, setRows, idx, key, value) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: value };
    setRows(copy);
  };
  const addRow = (rows, setRows, blank) => setRows([...rows, { ...blank }]);
  const removeRow = (rows, setRows, idx, blank) => {
    const copy = [...rows];
    copy.splice(idx, 1);
    setRows(copy.length ? copy : [{ ...blank }]);
  };

  // Validation
  const validate = () => {
    const e = {};
    // Only require the first field (legalFirst) for demo purposes
    if (!String(form.legalFirst || '').trim()) {
      e.legalFirst = 'Required';
    }

    // Keep format validation for fields that are filled in (but don't require them)
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';

    if (form.mobile && !isTenPhone(form.mobile)) e.mobile = '10 digits';

    if (form.homeState && !isTwoLetterState(form.homeState)) e.homeState = 'Use 2-letter code';
    if (form.homeZip && !isFiveZip(form.homeZip)) e.homeZip = '5-digit ZIP';

    if (!form.mailingSameAsHome) {
      if (form.mailingState && !isTwoLetterState(form.mailingState)) e.mailingState = 'Use 2-letter code';
      if (form.mailingZip && !isFiveZip(form.mailingZip)) e.mailingZip = '5-digit ZIP';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 500);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">GTS State University — Graduate Admission Application</h4>
              <p className="gts-form-overview">
                Apply for master’s or doctoral study at GTSU. This is a <strong>demo-only</strong> submission—no data is stored or transmitted.
              </p>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions">
                  <button className="btn btn-secondary" onClick={() => window.print()}>Print this page</button>
                  <Link to="/forms" className="btn btn-secondary">Back to Forms &amp; Applications</Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Program */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Program Selection</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Start Term <span className="gts-required">*</span></span>
                      <select name="startTerm" value={form.startTerm} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Fall 2025</option>
                        <option>Spring 2026</option>
                        <option>Summer 2026</option>
                      </select>
                      {errors.startTerm && <em className="gts-error">{errors.startTerm}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Degree Sought <span className="gts-required">*</span></span>
                      <select name="degreeSought" value={form.degreeSought} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>MS</option>
                        <option>MA</option>
                        <option>MEng</option>
                        <option>MBA</option>
                        <option>MPH</option>
                        <option>MEd</option>
                        <option>PhD</option>
                        <option>EdD</option>
                        <option>Other</option>
                      </select>
                      {errors.degreeSought && <em className="gts-error">{errors.degreeSought}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Program/Department <span className="gts-required">*</span></span>
                      <input name="programName" value={form.programName} onChange={onChange} placeholder="e.g., Computer Science (MS)" />
                      {errors.programName && <em className="gts-error">{errors.programName}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Concentration (optional)</span>
                      <input name="concentration" value={form.concentration} onChange={onChange} placeholder="e.g., Data Science" />
                    </label>

                    <label className="gts-field">
                      <span>Delivery</span>
                      <select name="delivery" value={form.delivery} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Campus</option>
                        <option>Online</option>
                        <option>Hybrid</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Funding Interest</span>
                      <select name="fundingInterest" value={form.fundingInterest} onChange={onChange}>
                        <option>None</option>
                        <option>Graduate Assistantship (GTA/GRA)</option>
                        <option>Fellowships</option>
                      </select>
                    </label>

                    <fieldset className="gts-field">
                      <legend>Plan to apply for financial aid (FAFSA)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="applyFinancialAid" value={o} checked={form.applyFinancialAid===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Personal */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Personal Information</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Legal First <span className="gts-required">*</span></span><input name="legalFirst" value={form.legalFirst} onChange={onChange} />{errors.legalFirst && <em className="gts-error">{errors.legalFirst}</em>}</label>
                    <label className="gts-field"><span>Middle</span><input name="legalMiddle" value={form.legalMiddle} onChange={onChange} /></label>
                    <label className="gts-field"><span>Legal Last <span className="gts-required">*</span></span><input name="legalLast" value={form.legalLast} onChange={onChange} />{errors.legalLast && <em className="gts-error">{errors.legalLast}</em>}</label>
                    <label className="gts-field"><span>Preferred Name</span><input name="preferredName" value={form.preferredName} onChange={onChange} /></label>

                    <label className="gts-field"><span>Date of Birth <span className="gts-required">*</span></span><input type="date" name="dob" value={form.dob} onChange={onChange} />{errors.dob && <em className="gts-error">{errors.dob}</em>}</label>
                    <label className="gts-field">
                      <span>Gender</span>
                      <select name="gender" value={form.gender} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Female</option>
                        <option>Male</option>
                        <option>Non-binary</option>
                        <option>Prefer not to answer</option>
                        <option>Other / Self-describe</option>
                      </select>
                    </label>
                    <label className="gts-field"><span>SSN (last 4, optional)</span><input name="ssnLast4" maxLength={4} value={form.ssnLast4} onChange={onChange} /></label>
                  </div>
                </div>

                {/* Contact & Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Contact &amp; Address</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field"><span>Email <span className="gts-required">*</span></span><input type="email" name="email" value={form.email} onChange={onChange} />{errors.email && <em className="gts-error">{errors.email}</em>}</label>
                    <label className="gts-field"><span>Mobile</span><input name="mobile" value={form.mobile} onChange={onChange} maxLength={14} />{errors.mobile && <em className="gts-error">{errors.mobile}</em>}</label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="allowText" checked={form.allowText} onChange={onChange} />
                      <span>Okay to text me about deadlines and updates</span>
                    </label>

                    <label className="gts-field gts-span-2"><span>Permanent Street <span className="gts-required">*</span></span><input name="homeStreet" value={form.homeStreet} onChange={onChange} /></label>
                    <label className="gts-field"><span>Apt/Suite</span><input name="homeApt" value={form.homeApt} onChange={onChange} /></label>
                    <label className="gts-field"><span>City <span className="gts-required">*</span></span><input name="homeCity" value={form.homeCity} onChange={onChange} /></label>
                    <label className="gts-field"><span>State (2) <span className="gts-required">*</span></span><input name="homeState" value={form.homeState} onChange={onChange} maxLength={2} placeholder="TN" />{errors.homeState && <em className="gts-error">{errors.homeState}</em>}</label>
                    <label className="gts-field"><span>ZIP (5) <span className="gts-required">*</span></span><input name="homeZip" value={form.homeZip} onChange={onChange} maxLength={5} />{errors.homeZip && <em className="gts-error">{errors.homeZip}</em>}</label>
                    {errors.address && <em className="gts-error">{errors.address}</em>}

                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="mailingSameAsHome" checked={form.mailingSameAsHome} onChange={onChange} />
                      <span>Mailing address is the same as permanent</span>
                    </label>

                    {!form.mailingSameAsHome && (
                      <>
                        <label className="gts-field gts-span-2"><span>Mailing Street</span><input name="mailingStreet" value={form.mailingStreet} onChange={onChange} /></label>
                        <label className="gts-field"><span>Apt/Suite</span><input name="mailingApt" value={form.mailingApt} onChange={onChange} /></label>
                        <label className="gts-field"><span>City</span><input name="mailingCity" value={form.mailingCity} onChange={onChange} /></label>
                        <label className="gts-field"><span>State (2)</span><input name="mailingState" value={form.mailingState} onChange={onChange} maxLength={2} /></label>
                        <label className="gts-field"><span>ZIP (5)</span><input name="mailingZip" value={form.mailingZip} onChange={onChange} maxLength={5} /></label>
                      </>
                    )}
                  </div>
                </div>

                {/* Citizenship / Residency */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Citizenship &amp; Residency</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Citizenship</span>
                      <select name="citizenship" value={form.citizenship} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>U.S. Citizen</option>
                        <option>Permanent Resident</option>
                        <option>Non-U.S. Citizen</option>
                      </select>
                    </label>
                    {form.citizenship && form.citizenship !== 'U.S. Citizen' && (
                      <>
                        <label className="gts-field"><span>Country of Citizenship</span><input name="countryCitizenship" value={form.countryCitizenship} onChange={onChange} /></label>
                        <label className="gts-field"><span>Current Visa Type (if any)</span><input name="visaType" value={form.visaType} onChange={onChange} /></label>
                      </>
                    )}

                    <fieldset className="gts-field">
                      <legend>Are you a resident of the State of GTS?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="gtsResident" value={o} checked={form.gtsResident===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Testing */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Testing</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="requestTestWaiver" checked={form.requestTestWaiver} onChange={onChange} />
                      <span>Request test waiver (GRE/GMAT). Provide scores below only if required by your program.</span>
                    </label>

                    {!form.requestTestWaiver && (
                      <>
                        <label className="gts-field"><span>GRE Verbal</span><input name="greVerbal" value={form.greVerbal} onChange={onChange} /></label>
                        <label className="gts-field"><span>GRE Quant</span><input name="greQuant" value={form.greQuant} onChange={onChange} /></label>
                        <label className="gts-field"><span>GRE A/W</span><input name="greAW" value={form.greAW} onChange={onChange} /></label>
                        <label className="gts-field"><span>GMAT Total</span><input name="gmatTotal" value={form.gmatTotal} onChange={onChange} /></label>
                        <label className="gts-field"><span>TOEFL Total</span><input name="toeflTotal" value={form.toeflTotal} onChange={onChange} /></label>
                        <label className="gts-field"><span>IELTS Overall</span><input name="ieltsOverall" value={form.ieltsOverall} onChange={onChange} /></label>
                        <label className="gts-field"><span>Most Recent Test Date</span><input type="date" name="testDate" value={form.testDate} onChange={onChange} /></label>
                      </>
                    )}
                    {errors.tests && <em className="gts-error">{errors.tests}</em>}
                  </div>
                </div>

                {/* Academic History */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Academic History</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Highest Degree Earned</span>
                      <select name="highestDegree" value={form.highestDegree} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>BA/BS</option>
                        <option>MA/MS</option>
                        <option>MBA/MEng/Other Master’s</option>
                        <option>Doctorate</option>
                        <option>Other / In Progress</option>
                      </select>
                    </label>
                    <label className="gts-field">
                      <span>Overall GPA (highest degree)</span>
                      <input name="highestGPA" value={form.highestGPA} onChange={onChange} />
                    </label>
                  </div>

                  <div className="gts-panel">
                    {colleges.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field gts-span-2"><span>Institution <span className="gts-required">*</span></span><input value={row.institution||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'institution',e.target.value)} /></label>
                        <label className="gts-field"><span>City</span><input value={row.city||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'city',e.target.value)} /></label>
                        <label className="gts-field"><span>State (2)</span><input value={row.state||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'state',e.target.value)} maxLength={2} /></label>
                        <label className="gts-field"><span>Degree</span><input value={row.degree||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'degree',e.target.value)} placeholder="e.g., BS, MS" /></label>
                        <label className="gts-field"><span>Major</span><input value={row.major||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'major',e.target.value)} /></label>
                        <label className="gts-field"><span>Dates Attended</span><input value={row.start||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'start',e.target.value)} placeholder="Start (e.g., 08/2021)" /></label>
                        <label className="gts-field"><span>Through</span><input value={row.end||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'end',e.target.value)} placeholder="End (e.g., 05/2025)" /></label>
                        <label className="gts-field"><span>GPA</span><input value={row.gpa||''} onChange={(e)=>updateRow(colleges,setColleges,idx,'gpa',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(colleges,setColleges,idx,{ institution:'', city:'', state:'', degree:'', major:'', start:'', end:'', gpa:'' })}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(colleges,setColleges,{ institution:'', city:'', state:'', degree:'', major:'', start:'', end:'', gpa:'' })}>Add Institution</button>
                    </div>
                    {errors.colleges && <em className="gts-error">{errors.colleges}</em>}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head">
                    <h5>Recommendations</h5>
                    <div className="gts-small-note">Most programs require 2–3 recommenders.</div>
                  </div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="includeRecs" checked={form.includeRecs} onChange={onChange} />
                      <span>I will provide recommender contact info now</span>
                    </label>
                  </div>

                  {form.includeRecs && (
                    <div className="gts-panel">
                      {recs.map((row, idx) => (
                        <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                          <label className="gts-field"><span>Name</span><input value={row.name||''} onChange={(e)=>updateRow(recs,setRecs,idx,'name',e.target.value)} /></label>
                          <label className="gts-field"><span>Email</span><input value={row.email||''} onChange={(e)=>updateRow(recs,setRecs,idx,'email',e.target.value)} /></label>
                          <label className="gts-field gts-span-2"><span>Relationship</span><input value={row.relationship||''} onChange={(e)=>updateRow(recs,setRecs,idx,'relationship',e.target.value)} /></label>
                          <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(recs,setRecs,idx,{ name:'', email:'', relationship:'' })}>Remove</button>
                          </div>
                        </div>
                      ))}
                      <div className="gts-actions">
                        <button type="button" className="btn btn-secondary" onClick={()=>addRow(recs,setRecs,{ name:'', email:'', relationship:'' })}>Add Recommender</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Employment History (optional) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Employment History (Optional)</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="includeEmployment" checked={form.includeEmployment} onChange={onChange} />
                      <span>Include recent employment</span>
                    </label>
                  </div>
                  {form.includeEmployment && (
                    <div className="gts-panel">
                      {employment.map((row, idx) => (
                        <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                          <label className="gts-field"><span>Employer</span><input value={row.employer||''} onChange={(e)=>updateRow(employment,setEmployment,idx,'employer',e.target.value)} /></label>
                          <label className="gts-field"><span>Title</span><input value={row.title||''} onChange={(e)=>updateRow(employment,setEmployment,idx,'title',e.target.value)} /></label>
                          <label className="gts-field"><span>Dates</span><input value={row.dates||''} onChange={(e)=>updateRow(employment,setEmployment,idx,'dates',e.target.value)} placeholder="e.g., 06/2022–Present" /></label>
                          <label className="gts-field gts-span-2"><span>Key responsibilities (short)</span><input value={row.responsibilities||''} onChange={(e)=>updateRow(employment,setEmployment,idx,'responsibilities',e.target.value)} /></label>
                          <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(employment,setEmployment,idx,{ employer:'', title:'', dates:'', responsibilities:'' })}>Remove</button>
                          </div>
                        </div>
                      ))}
                      <div className="gts-actions">
                        <button type="button" className="btn btn-secondary" onClick={()=>addRow(employment,setEmployment,{ employer:'', title:'', dates:'', responsibilities:'' })}>Add Position</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Essays / Materials */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Essays &amp; Materials</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>Statement of Purpose</span>
                      <textarea name="statementOfPurpose" rows={6} value={form.statementOfPurpose} onChange={onChange} placeholder="Academic background, goals, and fit for the program." />
                    </label>
                    <label className="gts-field gts-span-2">
                      <span>Research Interests (optional)</span>
                      <textarea name="researchInterests" rows={4} value={form.researchInterests} onChange={onChange} placeholder="Topics, faculty, labs of interest (if applicable)." />
                    </label>
                    <label className="gts-field gts-span-2">
                      <span>Résumé/CV link (optional)</span>
                      <input name="resumeLink" value={form.resumeLink} onChange={onChange} placeholder="URL to online résumé/CV" />
                    </label>
                  </div>
                </div>

                {/* Conduct */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Academic Conduct</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Have you been disciplined for academic or conduct violations?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map((o) => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="academicDiscipline" value={o} checked={form.academicDiscipline===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.academicDiscipline === 'Yes' && (
                      <label className="gts-field gts-span-2">
                        <span>Please explain (brief)</span>
                        <textarea name="academicDisciplineExplain" rows={3} value={form.academicDisciplineExplain} onChange={onChange} />
                      </label>
                    )}
                  </div>
                </div>

                {/* Declaration */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange} />
                      <span>
                        I certify the information provided is true and complete. I understand this is a demo and no official submission is processed.
                        <span className="gts-required"> *</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Applicant Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange} />
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange} />
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: 5 }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GraduateAdmissionApplication;
